/**
 * hook for creating new metadocs from selected documents
 * handles metadoc creation, processing state, and navigation to new metadoc
 */
import KaboodleToaster from "components/common/KaboodleToaster";
import type { MetadocNavigationContext } from "pages/metadoc/utils/metadocRoutes";
import { useCallback } from "react";
import { setShowDocumentsDrawer } from "reducers/drawer";
import {
  addProcessingMetadoc,
  removeProcessingMetadoc,
} from "reducers/metadoc";
import { useCreateMetadocMutation } from "services/metadoc-api";
import { useDispatch } from "utils/redux";

import * as Sentry from "@sentry/react";

/**
 * generates a default name for a metadoc
 * format: Metadoc_YYYYMMDD_HHMMSS_N
 * includes seconds for uniqueness and document count as suffix
 */
const generateMetadocName = (documentCount: number): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return `Metadoc_${year}${month}${day}_${hours}${minutes}_${documentCount}`;
};

type UseCreateMetadocConfig = {
  fileId: number;
  navigationContext?: MetadocNavigationContext;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
};

const createTemporaryMetadocId = (timestamp: number): number => timestamp * -1;

/**
 * hook for creating metadocs from selected documents
 * handles name generation, API call, Redux state updates, and notifications
 */
export const useCreateMetadoc = (config: UseCreateMetadocConfig) => {
  const { fileId, navigationContext, onSuccess, onError } = config;
  const dispatch = useDispatch();
  const [createMetadocMutation] = useCreateMetadocMutation();

  const createMetadoc = useCallback(
    async (documentIds: Set<number>) => {
      if (documentIds.size === 0) {
        KaboodleToaster.error("Please select at least one document");
        return;
      }

      // generate a default name for the metadoc
      const defaultName = generateMetadocName(documentIds.size);
      const createdAt = Date.now();
      const tempId = createTemporaryMetadocId(createdAt);

      // show toast immediately with temporary ID (will be updated once API returns)
      dispatch(
        addProcessingMetadoc({
          metadocId: tempId,
          fileId,
          name: defaultName,
          status: "pending",
          createdAt,
          navigationContext,
        }),
      );

      // close the drawer
      dispatch(setShowDocumentsDrawer(false));

      try {
        const response = await createMetadocMutation({
          name: defaultName,
          file_id: fileId,
          document_ids: Array.from(documentIds),
        }).unwrap();

        // remove temp toast and add real one
        dispatch(removeProcessingMetadoc(tempId));
        dispatch(
          addProcessingMetadoc({
            metadocId: response.id,
            fileId,
            name: defaultName,
            status: "pending",
            createdAt,
            navigationContext,
          }),
        );

        KaboodleToaster.success("Meta Document is being created");
        onSuccess?.();
      } catch (error) {
        // log to Sentry for monitoring
        Sentry.captureException(error, {
          tags: { feature: "metadoc", action: "metadoc_creation" },
          extra: { fileId, documentCount: documentIds.size },
        });
        // remove the temp processing toast on error
        dispatch(removeProcessingMetadoc(tempId));

        KaboodleToaster.error("Failed to create Meta Document");
        onError?.(error);
      }
    },
    [
      fileId,
      navigationContext,
      createMetadocMutation,
      dispatch,
      onSuccess,
      onError,
    ],
  );

  return { createMetadoc };
};
